import { Link } from 'react-router-dom';
import { Check, ChevronRight } from 'lucide-react';
import { Appointment } from '../services/patientApi';
import { StatusBadge } from './StatusBadge';
import { ConfirmationBadge } from './ConfirmationBadge';
import { PORTAL_ROUTES } from '../constants';
import { formatDate } from '../../utils/helpers';
import { canRequestConfirm } from '../utils/appointmentRequests';

interface AppointmentsTableProps {
  rows: Appointment[];
  /** ID of the appointment currently being confirmed, or null when idle. */
  confirmingId: string | null;
  onConfirm: (id: string) => void;
}

const HEADER_CELL =
  'text-left text-[11px] font-semibold text-secondary-400 uppercase tracking-wider px-3 py-3';

export const AppointmentsTable = ({ rows, confirmingId, onConfirm }: AppointmentsTableProps) => (
  <table className="w-full">
    <thead>
      <tr>
        <th className={`${HEADER_CELL} w-10`}>#</th>
        <th className={HEADER_CELL}>Provider</th>
        <th className={HEADER_CELL}>Date</th>
        <th className={HEADER_CELL}>Time</th>
        <th className={HEADER_CELL}>Type</th>
        <th className={HEADER_CELL}>Status</th>
        <th className={HEADER_CELL}>Confirmation</th>
        <th className={HEADER_CELL}>Actions</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row, i) => {
        const showConfirm = canRequestConfirm(row);
        const isConfirming = confirmingId === row.id;
        return (
          <tr
            key={row.id}
            className="border-t border-gray-100 hover:bg-gray-50/50 transition-colors"
          >
            <td className="px-3 py-3 text-sm text-secondary-400">{i + 1}</td>
            <td className="px-3 py-3">
              {row.provider.name ? (
                <span className="text-sm font-medium text-secondary-800">{row.provider.name}</span>
              ) : (
                <span className="inline-flex text-xs font-medium text-secondary-500 bg-gray-50 border border-dashed border-gray-300 rounded-md px-2.5 py-1">
                  Not assigned yet
                </span>
              )}
            </td>
            <td className="px-3 py-3 text-sm text-secondary-600">{formatDate(row.date)}</td>
            <td className="px-3 py-3 text-sm text-secondary-600">
              {row.startTime} &ndash; {row.endTime}
            </td>
            <td className="px-3 py-3">
              <span className="inline-flex text-xs font-medium text-secondary-600 bg-gray-50 border border-dashed border-gray-300 rounded-md px-2.5 py-1">
                {row.type || 'Appointment'}
              </span>
            </td>
            <td className="px-3 py-3">
              <StatusBadge status={row.status} />
            </td>
            <td className="px-3 py-3">
              <ConfirmationBadge appt={row} />
            </td>
            <td className="px-3 py-3">
              <div className="flex items-center gap-2">
                {showConfirm && (
                  <button
                    onClick={() => onConfirm(row.id)}
                    disabled={isConfirming}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 border border-green-300 bg-green-50 rounded-md px-3 py-1.5 hover:bg-green-100 transition-colors disabled:opacity-60 disabled:cursor-wait"
                  >
                    <Check size={13} />
                    {isConfirming ? 'Sending request…' : 'Confirm'}
                  </button>
                )}
                <Link
                  to={`${PORTAL_ROUTES.APPOINTMENTS}/${row.id}`}
                  className="inline-flex items-center gap-1.5 text-xs text-secondary-500 border border-gray-200 rounded-md px-3 py-1.5 hover:bg-gray-50 hover:text-secondary-800 transition-colors"
                >
                  Details
                  <ChevronRight size={13} />
                </Link>
              </div>
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);
